import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Link,
  ThemeProvider,
  createTheme,
  CssBaseline,
  AppBar,
  Toolbar,
  Button,
  IconButton,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme as useAppTheme } from '../context/ThemeContext';
import { getStudentLandingThemeOverrides, landingThemeConfig } from './LandingStudent/styles/landingThemeConfig';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MinimalFooter from './LandingStudent/components/MinimalFooter';
import { links } from '../config/links';

const PrivacyPolicy = () => {
  const lastUpdated = "June 22, 2025";
  const navigate = useNavigate();
  const location = useLocation();
  const { } = useAppTheme(); // darkMode available if needed
  const [showNav, setShowNav] = useState(false);
  
  // Get color theme from location state or localStorage (for dev mode persistence)
  const [colorTheme] = useState(() => {
    if (process.env.NODE_ENV === 'development') {
      return location.state?.colorTheme || localStorage.getItem('devColorTheme') || 'teal';
    }
    return 'teal';
  });

  // Determine which theme to use based on referrer or default to student theme
  const isFromEducators = location.state?.from === 'educators' || 
                          document.referrer.includes('/educators') || 
                          document.referrer.includes('/business');
  
  // Create theme based on context
  const theme = createTheme(
    isFromEducators ? {
      palette: {
        mode: 'light',
        ...landingThemeConfig.b2b,
      },
    } : getStudentLandingThemeOverrides(colorTheme)
  );

  useEffect(() => {
    const handleScroll = () => {
      setShowNav(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBackClick = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(links.internal.home);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Navigation Bar */}
      <AppBar
        position="fixed"
        sx={{
          background: theme.palette.mode === 'dark' 
            ? `linear-gradient(to bottom, ${theme.palette.background.default}ee, ${theme.palette.background.default}cc)`
            : `linear-gradient(to bottom, ${theme.palette.background.paper}ee, ${theme.palette.background.paper}cc)`,
          backdropFilter: 'blur(10px)',
          boxShadow: showNav ? 1 : 0,
          transition: 'all 0.3s ease',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ height: 70, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton 
                onClick={handleBackClick}
                sx={{ color: theme.palette.text.primary }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  cursor: 'pointer',
                  color: theme.palette.primary.main,
                }}
                onClick={() => navigate(links.internal.home)}
              >
                examr
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => navigate('/')}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              Back to Home
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ 
        pt: 12, 
        pb: 6, 
        backgroundColor: theme.palette.background.default, 
        minHeight: '100vh' 
      }}>
        <Container maxWidth="md">
        <Paper 
          elevation={theme.palette.mode === 'dark' ? 0 : 2} 
          sx={{ 
            p: { xs: 3, md: 5 },
            backgroundColor: theme.palette.mode === 'dark' 
              ? theme.palette.background.paper 
              : theme.palette.background.paper,
            border: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none',
          }}
        >
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: 700,
              background: theme.palette.mode === 'dark'
                ? `linear-gradient(45deg, ${theme.palette.text.primary} 30%, ${theme.palette.primary.main} 90%)`
                : theme.palette.text.primary,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: theme.palette.mode === 'dark' ? 'transparent' : theme.palette.text.primary,
            }}
          >
            Privacy Policy
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last updated: {lastUpdated}
          </Typography>

          <Divider sx={{ mb: 4, borderColor: theme.palette.divider }} />

          <Box sx={{ 
            '& h2': { 
              mt: 6, 
              mb: 3, 
              fontWeight: 600,
              color: theme.palette.text.primary,
            }, 
            '& h3': { 
              mt: 4, 
              mb: 2, 
              fontWeight: 500,
              color: theme.palette.text.primary,
            },
            '& p': {
              color: theme.palette.text.secondary,
              lineHeight: 1.7,
              mb: 2,
            },
            '& ul': {
              mb: 3,
            }
          }}>
            
            <Typography variant="h4" component="h2">
              1. Introduction
            </Typography>
            <Typography variant="body1" paragraph>
              examr.ai ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you visit our website 
              and use our services.
            </Typography>

            <Typography variant="h4" component="h2">
              2. Information We Collect
            </Typography>
            
            <Typography variant="h5" component="h3">
              2.1 Information You Provide Directly
            </Typography>
            <Typography variant="body1" paragraph>
              When you interact with our platform, we may collect:
            </Typography>
            <List sx={{ 
              '& .MuiListItem-root': { 
                pl: 0,
                '&::before': {
                  content: '"•"',
                  color: theme.palette.primary.main,
                  fontWeight: 'bold',
                  display: 'inline-block',
                  width: '1em',
                  marginLeft: '1em',
                }
              },
              '& .MuiListItemText-primary': {
                color: theme.palette.text.primary,
              },
              '& .MuiListItemText-secondary': {
                color: theme.palette.text.secondary,
                mt: 0.5,
              }
            }}>
              <ListItem>
                <ListItemText primary="Email address when you join our waitlist" />
              </ListItem>
              <ListItem>
                <ListItemText primary="User type (student, tutor, educator, administrator, or other)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Referral source information (how you heard about us)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Optional feedback about your study challenges" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Account information if you create an account (name, preferences)" />
              </ListItem>
            </List>

            <Typography variant="h5" component="h3">
              2.2 Automatically Collected Information
            </Typography>
            <Typography variant="body1" paragraph>
              We automatically collect certain information about your device and usage:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Analytics Data" 
                  secondary="We use PostHog analytics to collect page views, session duration, clicks, scroll depth, and general usage patterns to improve our service"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Demo Interaction Data" 
                  secondary="When you use our demo features, we track completion rates, questions answered, hint usage, AI chat interactions, and quiz performance to understand user engagement"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Technical Information" 
                  secondary="IP address, browser type, device information, operating system, and referrer URLs"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Session Recordings" 
                  secondary="We may record user sessions to understand how you interact with our platform, with sensitive inputs masked for privacy"
                />
              </ListItem>
            </List>

            <Typography variant="h4" component="h2">
              3. How We Use Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              We use the collected information for the following purposes:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="To provide and improve our services" />
              </ListItem>
              <ListItem>
                <ListItemText primary="To communicate with you about our platform and updates" />
              </ListItem>
              <ListItem>
                <ListItemText primary="To analyze usage patterns and optimize user experience" />
              </ListItem>
              <ListItem>
                <ListItemText primary="To conduct A/B testing and product development" />
              </ListItem>
              <ListItem>
                <ListItemText primary="To provide customer support" />
              </ListItem>
              <ListItem>
                <ListItemText primary="To comply with legal obligations" />
              </ListItem>
            </List>

            <Typography variant="h4" component="h2">
              4. Information Sharing and Disclosure
            </Typography>
            <Typography variant="body1" paragraph>
              We do not sell, trade, or rent your personal information to third parties. We may share your 
              information in the following circumstances:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Service Providers" 
                  secondary="We may share information with trusted third-party service providers (like PostHog for analytics) who assist us in operating our platform"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Legal Requirements" 
                  secondary="We may disclose information if required by law or to protect our rights and safety"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Business Transfers" 
                  secondary="In the event of a merger, acquisition, or sale of assets, your information may be transferred"
                />
              </ListItem>
            </List>

            <Typography variant="h4" component="h2">
              5. Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              We implement appropriate technical and organizational measures to protect your personal information 
              against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission 
              over the internet or electronic storage is 100% secure.
            </Typography>

            <Typography variant="h4" component="h2">
              6. Data Retention
            </Typography>
            <Typography variant="body1" paragraph>
              We retain your personal information for as long as necessary to provide our services and fulfill 
              the purposes outlined in this policy. Waitlist information is retained until you request removal 
              or until the service launches and transitions to full user accounts.
            </Typography>

            <Typography variant="h4" component="h2">
              7. Your Rights and Choices
            </Typography>
            <Typography variant="body1" paragraph>
              Depending on your location, you may have the following rights:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Access: Request information about the personal data we hold about you" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Correction: Request correction of inaccurate personal information" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Deletion: Request deletion of your personal information" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Opt-out: Unsubscribe from our communications at any time" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Data Portability: Request a copy of your data in a portable format" />
              </ListItem>
            </List>

            <Typography variant="h4" component="h2">
              8. Cookies and Tracking Technologies
            </Typography>
            <Typography variant="body1" paragraph>
              We use cookies and similar tracking technologies to enhance your experience. These technologies 
              help us remember your preferences, understand how you use our platform, and improve our services. 
              You can control cookie settings through your browser preferences.
            </Typography>

            <Typography variant="h4" component="h2">
              9. Third-Party Services
            </Typography>
            <Typography variant="body1" paragraph>
              Our platform uses third-party services including:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="PostHog" 
                  secondary="For analytics and user behavior tracking. Their privacy policy is available at posthog.com/privacy"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Cloud Infrastructure Providers" 
                  secondary="For hosting and data storage with appropriate security measures"
                />
              </ListItem>
            </List>

            <Typography variant="h4" component="h2">
              10. Children's Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              Our service is intended for users aged 13 and older. We do not knowingly collect personal 
              information from children under 13. If we become aware that we have collected personal 
              information from a child under 13, we will take steps to delete such information.
            </Typography>

            <Typography variant="h4" component="h2">
              11. International Data Transfers
            </Typography>
            <Typography variant="body1" paragraph>
              Your information may be transferred to and processed in countries other than your country of 
              residence. We ensure appropriate safeguards are in place to protect your information in 
              accordance with this privacy policy.
            </Typography>

            <Typography variant="h4" component="h2">
              12. Changes to This Privacy Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time. We will notify you of any material changes 
              by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </Typography>

            <Typography variant="h4" component="h2">
              13. Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </Typography>
            <Typography variant="body1" paragraph>
              Email: <Link 
                href={`mailto:${links.external.supportEmail}`}
                sx={{ 
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  }
                }}
              >
                feedback@zenmathmind.com
              </Link>
            </Typography>

            <Divider sx={{ my: 4, borderColor: theme.palette.divider }} />
            
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              This privacy policy is designed to be transparent about our data practices while ensuring 
              we can continue to improve our educational platform for students and educators.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
    
    {/* Footer */}
    <MinimalFooter />
    
    </ThemeProvider>
  );
};

export default PrivacyPolicy;
